import { z } from 'zod';
import { EngagementStatus } from './engagement.js';
import { OfferedDocument, OwnRunningContract } from './engagement-lifecycle.js';
import { dayAfter, firmDayOf, startOfFirmDay } from './firm-day.js';
import { isoDay, isoInstant } from './values.js';
/**
 * The client's answer to an offer in the portal (ADR-072 §§ 4-5): the confirmations given one by
 * one, the acceptance with its record, a decline - and a consumer's withdrawal (§ 356a BGB).
 */
/**
 * What the client confirms when accepting (ADR-072 § 4), each in a box of its own, none ticked
 * beforehand. `early_start` and `expiry_on_completion` are a consumer's own wish, never required.
 */
export const AcceptanceConfirmation = z
    .enum([
    'terms',
    'fee_agreement',
    'information',
    'withdrawal_instructions',
    'early_start',
    'expiry_on_completion',
])
    .meta({ id: 'AcceptanceConfirmation' });
export const acceptanceConfirmationLabel = {
    terms: 'Ich bin mit der Einbeziehung der Allgemeinen Auftragsbedingungen einverstanden.',
    fee_agreement: 'Ich schließe die Vergütungsvereinbarung ab.',
    information: 'Ich habe die Pflichtinformationen nach der DL-InfoV und die Datenschutzhinweise zur Kenntnis genommen.',
    withdrawal_instructions: 'Ich habe die Widerrufsbelehrung und das Muster-Widerrufsformular gelesen.',
    early_start: 'Ich verlange ausdrücklich, dass Sie vor Ende der Widerrufsfrist mit den Leistungen beginnen. Mir ist bekannt, dass ich bei einem Widerruf Wertersatz für die bis dahin erbrachten Leistungen schulde (§ 357a Abs. 2 BGB).',
    expiry_on_completion: 'Soweit Einzelleistungen vereinbart sind: Mir ist bekannt, dass mein Widerrufsrecht mit der vollständigen Erbringung der Leistung erlischt (§ 356 Abs. 5 BGB).',
};
/**
 * Which confirmations an offer asks for, by its documents: the terms only where the AAB are part
 * of it, the fee agreement only where it has one, the information always - and a consumer the
 * instructions on withdrawal; a consumer may also ask for an early start.
 */
export function confirmationsFor(documents, consumer) {
    const required = [];
    if (documents.includes('AAB'))
        required.push('terms');
    if (documents.includes('VERGV'))
        required.push('fee_agreement');
    required.push('information');
    if (consumer)
        required.push('withdrawal_instructions');
    return { required, optional: consumer ? ['early_start', 'expiry_on_completion'] : [] };
}
/** The document id of the acceptance record, kept with the offer's documents (ADR-072 § 4). */
export const ACCEPTANCE_RECORD = 'ANNAHME';
/** The length of the period of withdrawal in days (§ 355 Abs. 2 BGB). */
const WITHDRAWAL_DAYS = 14;
/**
 * The last day a consumer may withdraw (ADR-072 § 5): for services the period begins with the
 * conclusion (§ 355 Abs. 2 Satz 2 BGB, Gestaltungshinweis 1), the day of conclusion not counted
 * (§ 187 Abs. 1 BGB), and ends with the 14th day after it (§ 188 Abs. 1 BGB) - moved to the next
 * Monday when it falls on a Saturday or Sunday (§ 193 BGB). Public holidays and the longer
 * period for missing instructions (§ 356 Abs. 3 BGB) are not counted yet (ADR-072 § 5).
 */
export function withdrawalLastDay(concludedAt) {
    let day = firmDayOf(concludedAt);
    for (let n = 0; n < WITHDRAWAL_DAYS; n += 1)
        day = dayAfter(day);
    while ([0, 6].includes(new Date(`${day}T12:00:00Z`).getUTCDay()))
        day = dayAfter(day);
    return day;
}
/** The first instant a consumer may no longer withdraw: the end of the last day in Berlin. */
export const withdrawalEndsAt = (concludedAt) => startOfFirmDay(dayAfter(withdrawalLastDay(concludedAt)));
const version = z.int().positive();
const unique = (values) => new Set(values).size === values.length;
/** Accepting an offer: the version meant and every box the client ticked. */
export const EngagementAcceptInput = z
    .object({
    version,
    confirmations: z.array(AcceptanceConfirmation).max(6).refine(unique, 'Each once'),
})
    .strict()
    .meta({ id: 'EngagementAcceptInput' });
/** Declining an offer, with a reason if the client wants to give one. */
export const EngagementDeclineInput = z
    .object({ version, reason: z.string().trim().min(1).max(1000).nullable() })
    .strict()
    .meta({ id: 'EngagementDeclineInput' });
/**
 * A consumer's withdrawal through the portal (§ 356a Abs. 2 BGB): their name, the contract (its
 * version) and the address the receipt goes to.
 */
export const EngagementRevokeInput = z
    .object({ version, name: z.string().trim().min(2).max(200), email: z.email().max(254) })
    .strict()
    .meta({ id: 'EngagementRevokeInput' });
/** How a withdrawal reached the firm: through the portal, or by other means it records. */
export const RevocationChannel = z
    .enum(['portal', 'letter', 'email', 'other'])
    .meta({ id: 'RevocationChannel' });
export const revocationChannelLabel = {
    portal: 'Widerrufsfunktion im Portal',
    letter: 'Brief',
    email: 'E-Mail',
    other: 'Anderer Weg',
};
/** A withdrawal that reached the firm by other means (ADR-072 § 5), recorded by the firm. */
export const EngagementRevocationInput = z
    .object({
    version,
    receivedOn: isoDay,
    channel: RevocationChannel.exclude(['portal']),
    note: z.string().trim().min(1).max(500).nullable(),
})
    .strict()
    .meta({ id: 'EngagementRevocationInput' });
/** How an offer was accepted: when, by which account, with what (ADR-072 § 4). */
export const EngagementAcceptance = z
    .object({
    acceptedAt: isoInstant,
    /** The portal account, by Keycloak `sub`. */
    account: z.string(),
    personName: z.string().nullable(),
    email: z.string().nullable(),
    confirmations: z.array(AcceptanceConfirmation),
    /** The documents accepted, with the SHA-256 they had. */
    documents: z.array(z.object({ id: z.string(), sha256: z.string() })),
    /** A consumer's last day to withdraw; `null`: no right of withdrawal. */
    withdrawalUntil: isoDay.nullable(),
})
    .meta({ id: 'EngagementAcceptance' });
/** A consumer's withdrawal: when it was received, how, from whom (ADR-072 § 5). */
export const EngagementRevocation = z
    .object({
    receivedAt: isoInstant,
    channel: RevocationChannel,
    name: z.string(),
    email: z.string().nullable(),
    /** Who recorded it: the portal account, or the firm's person (Keycloak `sub`). */
    recordedBy: z.string(),
    note: z.string().nullable(),
})
    .meta({ id: 'EngagementRevocation' });
/**
 * The lock of an offer in a mandate carried on after its end without a new intake, until the
 * update intake Kanos began with it completed (ADR-078 § 5, decided 2026-11-03): the task key of
 * `STBV-GWG-04` in the pack `stb-vertrag`.
 */
export const UPDATE_INTAKE_LOCK = 'annahme_aktualisierung';
/** A service of the offer with its price, shown right above the button (§ 312j Abs. 2 BGB). */
const OfferedService = z.object({ title: z.string(), price: z.string() });
/**
 * The offer as the client sees it in the portal (ADR-072 § 4): the newest version the firm
 * offered - never a draft - with its documents, what to confirm, what still keeps the client
 * from accepting, and how it was answered.
 */
export const PortalEngagement = z
    .object({
    offer: z
        .object({
        version,
        status: EngagementStatus,
        consumer: z.boolean(),
        firmName: z.string(),
        clientName: z.string(),
        issuedAt: isoInstant,
        offerValidUntil: isoDay,
        /** Open, not lapsed and not answered. */
        open: z.boolean(),
        documents: z.array(OfferedDocument),
        services: z.array(OfferedService),
        serviceStart: isoDay.nullable(),
        confirmations: z.object({
            required: z.array(AcceptanceConfirmation),
            optional: z.array(AcceptanceConfirmation),
        }),
        /** What keeps the client from accepting (ADR-072 § 4 lock), by the pack's task keys. */
        locks: z.array(z.object({ key: z.string(), title: z.string() })),
        acceptedAt: isoInstant.nullable(),
        declinedAt: isoInstant.nullable(),
        /** A consumer's last day to withdraw, once accepted. */
        withdrawalUntil: isoDay.nullable(),
        /** Whether the withdrawal function is open now (§ 356a Abs. 1 BGB). */
        revocable: z.boolean(),
        revokedAt: isoInstant.nullable(),
    })
        .nullable(),
    /** The contract that applies - in force, terminated or ended - once there is one. */
    contract: OwnRunningContract.nullable(),
})
    .meta({ id: 'PortalEngagement' });
/** The receipt of a withdrawal (§ 356a Abs. 4 BGB), shown at once and sent by mail. */
export const RevocationReceipt = z
    .object({
    receivedAt: isoInstant,
    name: z.string(),
    email: z.string(),
    contract: z.string(),
})
    .meta({ id: 'RevocationReceipt' });
//# sourceMappingURL=engagement-acceptance.js.map